import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { Button, Card, CardContent, Checkbox, Input, Label, Textarea } from "@/ui";
import { ArrowLeft, Calendar, Clock, Users, Building, User } from 'lucide-react';
import { apiUrl } from "@/constants";

interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  
  // Company Information
  companyName: string;
  companyAddress: string;
  contactPhone: string;
  
  // Additional Information
  specificNeeds: string;
  currentChallenges: string;
  howHeard: string;
  
  // Consent
  consent: boolean;
  marketingConsent: boolean;
}

interface FormErrors {
  [key: string]: string | undefined;
}

export default function BookDemo() {
  const [, setLocation] = useLocation();
  
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    companyName: '',
    companyAddress: '',
    contactPhone: '',
    specificNeeds: '',
    currentChallenges: '',
    howHeard: '',
    consent: false,
    marketingConsent: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Required fields
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.companyAddress.trim()) newErrors.companyAddress = 'Company address is required';
    if (!formData.contactPhone.trim()) newErrors.contactPhone = 'Contact phone is required';
    if (!formData.consent) newErrors.consent = 'You must agree to the terms and conditions';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (formData.contactPhone && !phoneRegex.test(formData.contactPhone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.contactPhone = 'Please enter a valid contact phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(apiUrl('/api/customers/demo-request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit demo request');
      }

      // Redirect to thank you page
      setLocation('/demo/thank-you');
    } catch (error) {
      console.error('Error submitting demo request:', error);
      alert(`There was an error submitting your demo request: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-12 text-center">
          <Button
            variant="ghost"
            onClick={() => setLocation('/')}
            className="mb-6 flex items-center mx-auto hover:scale-105 transition-transform"
            style={{ color: "#1356a6" }}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-5xl md:text-6xl font-normal mb-4" style={{ color: "#1356a6", fontFamily: "Montserrat, system-ui, sans-serif" }}>
            Sign up for Live Demo
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto" style={{ color: "#64748b", fontFamily: "Montserrat, system-ui, sans-serif" }}>
            Sign up to receive updates and schedule a personalized demo of our platform.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Main Form Card */}
          <Card className="border-2 hover:shadow-xl transition-shadow duration-300 mb-8">
            <CardContent className="p-8 md:p-12 space-y-10">
              
              {/* Personal Information Section */}
              <div>
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                  <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                    <User className="h-6 w-6" style={{ color: "#1356a6" }} />
                  </div>
                  <h2 className="text-2xl font-semibold" style={{ color: "#1356a6" }}>Personal Information</h2>
                </div>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={errors.firstName ? 'border-red-500' : ''}
                      />
                      {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={errors.lastName ? 'border-red-500' : ''}
                      />
                      {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={errors.phone ? 'border-red-500' : ''}
                      />
                      {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="jobTitle">Job Title *</Label>
                    <Input
                      id="jobTitle"
                      value={formData.jobTitle}
                      onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                      className={errors.jobTitle ? 'border-red-500' : ''}
                    />
                    {errors.jobTitle && <p className="text-red-500 text-sm mt-1">{errors.jobTitle}</p>}
                  </div>
                </div>
              </div>

              {/* Company Information Section */}
              <div>
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                  <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                    <Building className="h-6 w-6" style={{ color: "#1356a6" }} />
                  </div>
                  <h2 className="text-2xl font-semibold" style={{ color: "#1356a6" }}>Company Information</h2>
                </div>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      className={errors.companyName ? 'border-red-500' : ''}
                    />
                    {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="companyAddress">Company Address *</Label>
                    <Textarea
                      id="companyAddress"
                      value={formData.companyAddress}
                      onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                      placeholder="Enter your company's full address..."
                      className={errors.companyAddress ? 'border-red-500' : ''}
                      rows={3}
                    />
                    {errors.companyAddress && <p className="text-red-500 text-sm mt-1">{errors.companyAddress}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="contactPhone">Contact Phone Number *</Label>
                    <Input
                      id="contactPhone"
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                      placeholder="Enter contact phone number..."
                      className={errors.contactPhone ? 'border-red-500' : ''}
                    />
                    {errors.contactPhone && <p className="text-red-500 text-sm mt-1">{errors.contactPhone}</p>}
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div>
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                  <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                    <Users className="h-6 w-6" style={{ color: "#1356a6" }} />
                  </div>
                  <h2 className="text-2xl font-semibold" style={{ color: "#1356a6" }}>Additional Information</h2>
                </div>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="specificNeeds">Specific Needs or Use Cases</Label>
                    <Textarea
                      id="specificNeeds"
                      value={formData.specificNeeds}
                      onChange={(e) => handleInputChange('specificNeeds', e.target.value)}
                      placeholder="Tell us about your specific needs or use cases..."
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="currentChallenges">Current Challenges</Label>
                    <Textarea
                      id="currentChallenges"
                      value={formData.currentChallenges}
                      onChange={(e) => handleInputChange('currentChallenges', e.target.value)}
                      placeholder="What challenges are you currently facing in your healthcare operations?"
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="howHeard">How did you hear about us?</Label>
                    <select
                      id="howHeard"
                      value={formData.howHeard}
                      onChange={(e) => handleInputChange("howHeard", e.target.value)}
                      className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <option value="" disabled>Select an option</option>
                      <option value="Website">Website</option>
                      <option value="Google Search">Google Search</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Referral">Referral</option>
                      <option value="Conference">Conference</option>
                      <option value="Webinar">Webinar</option>
                      <option value="Email Marketing">Email Marketing</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
              
            </CardContent>
          </Card>

          {/* Consent and Submit */}
          <div className="space-y-8">
            {/* Consent */}
            <Card className="border-2 shadow-lg">
              <CardContent className="p-8 pt-6">
                <div className="space-y-4 max-w-2xl mx-auto">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="consent"
                      checked={formData.consent}
                      onChange={(e) => handleInputChange("consent", (e.target as HTMLInputElement).checked)}
                      className="flex-shrink-0"
                    />
                    <Label htmlFor="consent" className="text-sm">
                      I agree to the <a href="#" className="text-primary hover:underline">Terms of Service</a> and <a href="#" className="text-primary hover:underline">Privacy Policy</a> *
                    </Label>
                  </div>
                  {errors.consent && <p className="text-red-500 text-sm text-center">{errors.consent}</p>}
                  
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="marketingConsent"
                      checked={formData.marketingConsent}
                      onChange={(e) => handleInputChange("marketingConsent", (e.target as HTMLInputElement).checked)}
                      className="flex-shrink-0"
                    />
                    <Label htmlFor="marketingConsent" className="text-sm">
                      I would like to receive updates about Aiscendus products and services
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-center">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="px-12 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                style={{ 
                  backgroundColor: "#1356a6",
                  color: "white"
                }}
              >
                {isSubmitting ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Calendar className="h-4 w-4 mr-2" />
                    Book Demo
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
